import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { Message } from "../services/types";

/**
 * Agent Chat Store
 * Manages the state of agent chat conversations
 */

export interface AgentChatState {
  // Current conversation state
  messages: Message[];
  threadId: string;
  isLoading: boolean;
  thinkingStep: string | null;
  
  // Active stream tracking (to prevent loss during navigation)
  activeStreamController: AbortController | null;
  pendingMessageId: string | null; // ID of message being streamed
  
  // Actions
  setMessages: (messages: Message[]) => void;
  addMessage: (message: Message) => void;
  updateMessage: (messageId: string, content: string) => void;
  appendToMessage: (messageId: string, content: string) => void;
  setThreadId: (threadId: string) => void;
  setIsLoading: (isLoading: boolean) => void;
  setThinkingStep: (thinkingStep: string | null) => void;
  setActiveStream: (controller: AbortController | null, messageId: string | null) => void;
  
  // Clear all (reset chat)
  clearChat: () => void;
}

const initialState = {
  messages: [],
  threadId: "",
  isLoading: false,
  thinkingStep: null,
  activeStreamController: null,
  pendingMessageId: null,
};

export const useAgentChatStore = create<AgentChatState>()(
  devtools(
    (set, get) => ({
      ...initialState,
      
      setMessages: (messages: Message[]) => {
        set({ messages });
      },
      
      addMessage: (message: Message) => {
        set((state) => ({
          messages: [...state.messages, message],
        }));
      },
      
      updateMessage: (messageId: string, content: string) => {
        set((state) => ({
          messages: state.messages.map((msg) =>
            msg.id === messageId ? { ...msg, content } : msg
          ),
        }));
      },
      
      appendToMessage: (messageId: string, content: string) => {
        set((state) => ({
          messages: state.messages.map((msg) =>
            msg.id === messageId ? { ...msg, content: msg.content + content } : msg
          ),
        }));
      },
      
      setThreadId: (threadId: string) => {
        set({ threadId });
      },
      
      setIsLoading: (isLoading: boolean) => {
        set({ isLoading });
      },
      
      setThinkingStep: (thinkingStep: string | null) => {
        set({ thinkingStep });
      },
      
      setActiveStream: (controller: AbortController | null, messageId: string | null) => {
        set({ 
          activeStreamController: controller,
          pendingMessageId: messageId 
        });
      },
      
      clearChat: () => {
        // Abort any active stream before clearing
        const { activeStreamController } = get();
        if (activeStreamController) {
          activeStreamController.abort();
        }
        set(initialState);
      },
    }),
    {
      name: "agent-chat-store",
    }
  )
);
